import { useState, useRef } from 'react';
import Lightbox from 'yet-another-react-lightbox';
import Captions from 'yet-another-react-lightbox/plugins/captions';
import Video from 'yet-another-react-lightbox/plugins/video';
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/captions.css';

const ParticipantsTable = ({ 
  participants, 
  challenges, 
  currentMonth,
  calculateTotal, 
  openModal 
}) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [searchTerm, setSearchTerm] = useState('');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [slides, setSlides] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const calculateTableStats = (participantsList) => {
    let totalRuns = 0;
    let totalMiles = 0;
    
    participantsList.forEach(participant => {
      Object.values(participant.months).forEach(month => {
        if (month && month.body) {
          totalRuns++;
          totalMiles += parseFloat(month.body);
        }
      });
    });
    
    return { totalRuns, totalMiles: Math.round(totalMiles) };
  };

  const isVideo = (url) => {
    if (!url) return false;
    const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov'];
    const lowerUrl = url.toLowerCase();
    return videoExtensions.some(ext => lowerUrl.includes(ext));
  };

  const getVideoType = (url) => {
    const lowerUrl = url.toLowerCase();
    if (lowerUrl.includes('.webm')) return 'video/webm';
    if (lowerUrl.includes('.ogg')) return 'video/ogg';
    if (lowerUrl.includes('.mov')) return 'video/mp4';
    return 'video/mp4';
  };

  const setComment = (comment) => {
    if (comment && comment.trim() !== '') {
      return ` Runner comment - ${comment}`;
    }
    return '';
  }

  const buildSlide = (participant, month, monthData) => {
    const url = monthData.image_url;
    return isVideo(url)
      ? {
          type: "video",
          sources: [
            {
              src: url,
              type: getVideoType(url)
            }
          ],
          title: `${participant.participantName} - Month ${month}`,
          description: `Runner comment: ${monthData.body}`,
          autoPlay: false,
          controls: true,
          playsInline: true,
          muted: false,
          preload: "metadata",
          poster: url.replace(/\.(mp4|webm|ogg|mov)$/i, '.jpg')
        }
      : {
          src: url,
          title: `${participant.participantName} - Month ${month}`,
          description: `${setComment(monthData.body)}`
        };
  };

  // Build one slide per month this runner has uploaded an image/video for,
  // in month order, so the lightbox arrows step through that runner's months.
  const openLightbox = (participant, month) => {
    const runnerSlides = [];
    let startIndex = 0;

    for (let m = 1; m <= 12; m++) {
      const monthData = participant.months[`month${m}`];
      if (monthData && monthData.image_url) {
        if (m === month) startIndex = runnerSlides.length;
        runnerSlides.push(buildSlide(participant, m, monthData));
      }
    }

    setSlides(runnerSlides);
    setLightboxIndex(startIndex);
    setLightboxOpen(true);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const VideoRenderer = ({ slide }) => {
    const ref = useRef(null);

    return (
      <div
        style={{ width: "100%", height: "100%" }}
        onClick={() => {
          if (ref.current) {
            ref.current.play().catch(() => {});
          }
        }}
      >
        <video
          ref={ref}
          controls
          playsInline
          muted={false}
          webkit-playsinline="true"
          preload="metadata"
          style={{
            width: "100%",
            height: "100%",
            maxWidth: "100%",
            maxHeight: "100%"
          }}
        >
          {slide.sources?.map((source, index) => (
            <source key={index} src={source.src} type={source.type} />
          ))}
        </video>
      </div>
    );
  };

  const getFilteredAndSortedParticipants = () => {
    let filtered = participants.filter(participant =>
      participant.participantName.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    if (sortConfig.key === 'name') {
      filtered.sort((a, b) => {
        const nameA = a.participantName.toLowerCase();
        const nameB = b.participantName.toLowerCase();
        return sortConfig.direction === 'asc' 
          ? nameA.localeCompare(nameB)
          : nameB.localeCompare(nameA);
      });
    } else if (sortConfig.key === 'runs') {
      filtered.sort((a, b) => {
        return sortConfig.direction === 'asc'
          ? b.totalRuns - a.totalRuns
          : a.totalRuns - b.totalRuns;
      });
    } else if (sortConfig.key === 'miles') {
      filtered.sort((a, b) => {
        const milesA = calculateTotal(b.months);
        const milesB = calculateTotal(a.months);
        return sortConfig.direction === 'asc'
          ? milesA - milesB
          : milesB - milesA;
      });
    }
    
    return filtered;
  };

  const isOverTon = (passedMiles) => {
    if (parseInt(passedMiles) >= 100) {
      return "overOneTon"
    } else {
      return ""
    }
  };

  const isInactive = (participant) => {
    if (currentMonth <= 1) return false;
    for (let m = 1; m < currentMonth; m++) {
      if (!participant.months[`month${m}`]) return true;
    }
    return false;
  };

  const countCompletedMonths = (participant) => {
    let count = 0;
    for (let m = 1; m <= currentMonth; m++) {
      if (participant.months[`month${m}`]) count++;
    }
    return count;
  };

  const sortByCompletion = (list) => {
    const active = list.filter(p => !isInactive(p));
    const inactive = list.filter(p => isInactive(p));
    inactive.sort((a, b) => {
      const monthsDiff = countCompletedMonths(b) - countCompletedMonths(a);
      if (monthsDiff !== 0) return monthsDiff;
      return a.participantName.localeCompare(b.participantName);
    });
    return [...active, ...inactive];
  };

  const filteredParticipants = getFilteredAndSortedParticipants();

  // Separate participants by distance, with incomplete participants sorted to the bottom
  const participants10 = sortByCompletion(filteredParticipants.filter(p => p.participantDistance === 10));
  const participants20 = sortByCompletion(filteredParticipants.filter(p => p.participantDistance === 20));

  const monthNames = [ 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct','Nov', 'Dec'];

  const renderTable = (participantsList, title) => {
    const stats = calculateTableStats(participantsList);
    
    return (
      <div>
        <h2 className='milesIntro'>{title}</h2>
        <table className="runners">
          <thead>
            <tr className="headerTable">
              <th 
              className="alignLeft participantName"
                // className="alignLeft participantName sorted" 
                // onClick={() => handleSort('name')}
                // style={{ cursor: 'pointer' }}
              >
                Participant
              </th>
              {[...Array(12)].map((_, i) => {
                const current = i + 1;
                const challengeCount = currentMonth;
                console.log(currentMonth)
                let extraClass = "";
                if (current === challengeCount) {
                  extraClass = "equal-col";
                } else if (current > challengeCount) {
                  extraClass = "future-col";
                }

                return (
                  <th key={i} className={`mile-col month-col-${i + 1} ${extraClass}`}>
                    <span className="month-full">{monthNames[i]}</span>
                    <span className="month-abbrev">{monthNames[i][0]}</span>
                  </th>
                );
              })}
              
              <th className="mobile-info-btn">
                
              </th>
            </tr>
          </thead>
          <tbody>
            {participantsList.map((participant, idx) => (
              <tr key={idx} className={`${isOverTon(calculateTotal(participant.months))} ${isInactive(participant) ? 'incomplete-participant' : ''}`}>
                <td className="runnerName bottomBorder">
                  {participant.participantName}
                </td>
              
                {[...Array(12)].map((_, i) => {
                  const monthKey = `month${i + 1}`;
                  const monthData = participant.months[monthKey];
                  const current = i + 1;
                  const challengeCount = currentMonth;

                  let extraClass = "";
                  if (current === challengeCount) {
                    extraClass = "equal-col-data";
                  } else if (current > challengeCount) {
                    extraClass = "future-col-data";
                  }
                  
                  return (
                    <td key={i} className={`mile-col-data month-col-${i + 1} bottomBorder ${extraClass}`}>
                      {monthData ? (
                        <>
                          {monthData.image_url ? (
                            <div
                              onClick={() => openLightbox(participant, i + 1)}
                              className="mileButton"
                            >
                              ✔️
                            </div>
                          ) : (
                            <span className="noPhoto">
                              ✔️
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="text-gray-300">-</span>
                      )}
                    </td>
                  );
                })}
          
              </tr>
            ))}
          </tbody>
        </table>
     
      </div>
    );
  };

  return (
    <>
      <style>{`
        .month-full {
          display: inline;
        }
        .month-abbrev {
          display: none;
        }
        
        @media (max-width: 600px) {
          .month-full {
            display: none;
          }
          .month-abbrev {
            display: inline;
          }
        }
      `}</style>

      <div className='searchRunners'>
        <input
          type="text"
          placeholder="Search participants..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className='searchBox'
        />
        {searchTerm && (
          <span style={{ marginLeft: '1rem', color: '#666' }}>
            Showing {filteredParticipants.length} of {participants.length} participants
          </span>
        )}
      </div>

      {renderTable(participants10, 'TEN MILERS')}
      {renderTable(participants20, 'TWENTY MILERS')}

      
      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        slides={slides}
        index={lightboxIndex}
        on={{ view: ({ index }) => setLightboxIndex(index) }}
        carousel={{ finite: true }}
        plugins={[Captions, Video]}
        video={{ 
          autoPlay: false, 
          controls: true,
          playsInline: true,
          muted: false,
          preload: "metadata"
        }}
        render={{
          video: VideoRenderer
        }}
      />
    </>
  );
};

export default ParticipantsTable;